import { routeTypesEnum } from "helpers/enums";
import {
    actualLineStyle,
    actualMarkerStyle,
    coordsToSegments,
    emulatedEmptyDashLineStyle,
    emulatedLineStyle,
    factEmptyDashLineStyle,
    factLineStyle,
    factMarkerStyle,
    mobileFactLineStyle,
    mobileMarkerStyle,
    planDashLineStyle,
    planLineStyle,
    taskMarkerStyle,
} from "helpers/map";
import { divIcon } from "leaflet";
import isEqual from "lodash/isEqual";
import React, { Component, Fragment } from "react";
import { CircleMarker, Marker, Polyline, Popup, Tooltip } from "react-leaflet";
import { useMap, useMapEvent } from "react-leaflet/hooks";
import { deleteEmulatedSegment } from "sources/books/bids";
import PolylineDecorator from "./polylineDecorator";

const MapMoveHandler = (props) => {
    const map = useMap();
    useMapEvent("mousemove", ({ latlng }) => {
        if (!props.mouseOnLine) {
            return;
        }

        const mapPoint = props.lines[props.mouseOnLine].closestLayerPoint(map.latLngToLayerPoint(latlng));
        const mapLatLngPoint = map.layerPointToLatLng(mapPoint);
        props.setLinePoint([mapLatLngPoint.lng, mapLatLngPoint.lat]);
    });
    return null;
};

const isNotNullArray = (value) => value && value.length && Array.isArray(value);

const RoutePoints = React.memo((props) => {
    const { routePoints, organizationTimezone } = props;
    if (!isNotNullArray(routePoints)) {
        return null;
    }
    return routePoints.map((route, index) => {
        const { points, isMobile, hasActiveMileage } = route;
        const markerStyle = hasActiveMileage ? (isMobile ? mobileMarkerStyle : factMarkerStyle) : actualMarkerStyle;
        if (!isNotNullArray(points)) {
            return null;
        }
        return points.map((point, i) => (
            <RoutePoint
                key={`${index}_${i}`}
                point={point}
                organizationTimezone={organizationTimezone}
                markerStyle={markerStyle}
            />
        ));
    });
});

const RoutePoint = ({ point, organizationTimezone, markerStyle }) => {
    const { fixedAt, coordinate } = point;
    let fixedTime = new Date(fixedAt);
    if (organizationTimezone) {
        fixedTime = fixedTime.toLocaleString("ru-RU", { timeZone: organizationTimezone.id });
    }
    if (!(coordinate && coordinate[0] && coordinate[1])) {
        return null;
    }
    return (
        <CircleMarker center={[coordinate[1], coordinate[0]]} {...markerStyle}>
            <Tooltip className="map__tooltip--default" direction="top">
                {fixedTime}
                {organizationTimezone && (
                    <>
                        <br />
                        {organizationTimezone.displayName}
                    </>
                )}
            </Tooltip>
        </CircleMarker>
    );
};

export default class Route extends Component {
    state = {
        totalRoute: [],
        supportPoints: [],
        polylinePoint: null,
        selectGeozonePoint: undefined,
        mouseOnLine: false,
        selectedEmulatedSegment: undefined,
    };

    componentDidMount() {
        const callback = (totalRoute) => {
            // т к действие происходит во время рендеринга объекта ref для компонента Map ещё не существует
            // откладываем действие, чтобы оно произошло после отрисовки
            if (totalRoute && totalRoute.length) {
                this.props.changeMapPosition(totalRoute);
            }
        };
        this.initRoute(this.props, callback);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!isEqual(this.selectMapObjectIds(this.props), this.selectMapObjectIds(nextProps))) {
            this.initRoute(nextProps);
        }
    }

    selectMapObjectIds = (props) => {
        return props.route.segments.reduce((memo, segment) => {
            memo.push(segment.mapObjectFrom?.id, segment.mapObjectTo?.id);
            return memo;
        }, []);
    };

    initRoute = (props, callback = null) => {
        const { totalRoute, supportPoints, emptyMileageRoute, tollSegments } = coordsToSegments(
            props.route.segments,
            props.route.tollRoadSegments,
        );
        this.setState({ totalRoute, supportPoints, emptyMileageRoute, tollSegments });
        callback && callback(totalRoute);
    };

    supportPoints = () => {
        const props = {
            icon: divIcon({ className: "route-icon route-icon--between", iconSize: [15, 15] }),
        };
        const { supportPoints } = this.state;
        const pointRenderer = (point, index) => <Marker key={index} position={point} {...props} zIndexOffset={-250} />;

        return supportPoints.map(pointRenderer);
    };

    tollRoads = () =>
        this.state.tollSegments.map((item, i) => (
            <Polyline key={i} positions={item.positions} weight={5} color={item.color} />
        ));

    polylineEvents = (type) => {
        const { selectGeozoneMode } = this.props;
        if (!selectGeozoneMode || selectGeozoneMode === "form") {
            return {};
        }
        return {
            mouseover: () => this.setState({ mouseOnLine: type }),
            mouseout: () => {
                setTimeout(() => {
                    this.setState({ selectGeozonePoint: undefined, mouseOnLine: false });
                }, 0);
            },
            click: () => {
                this.props.setGeozonePoint(this.state.selectGeozonePoint);
                this.setState({ selectGeozonePoint: undefined, mouseOnLine: false });
            },
        };
    };

    removeEmulatedSegment = (segmentId, bidId) => {
        this.props.mergeMapData?.({ isLoading: true });
        deleteEmulatedSegment({ id: segmentId, bidId }).then((res) => {
            if (res) {
                this.props.updateCarRoutes();
            }
            this.props.mergeMapData?.({ isLoading: false });
        });
    };

    render() {
        const {
            traveledRoute,
            routePoints,
            organizationTimezone,
            zoom,
            filters,
            expandedRangeTime,
            selectGeozoneMode,
            roles,
            bidId,
        } = this.props;
        const { isRoot, isTruckingOrganizationAdmin, isTruckingOrganizationManager, isTruckingOrganizationHeadLogist } =
            roles || {};
        const { mobile, tracker, tollRoads } = filters || {};
        const { totalRoute, emptyMileageRoute, selectGeozonePoint } = this.state;
        if (!(totalRoute && totalRoute.length)) {
            return null;
        }

        return (
            <>
                <Polyline
                    ref={(el) => (this.emptyLine = el)}
                    positions={emptyMileageRoute}
                    {...planDashLineStyle}
                    {...(selectGeozoneMode ? { dashArray: undefined } : {})}
                    eventHandlers={this.polylineEvents("empty")}
                />
                <Polyline
                    ref={(el) => (this.mainLine = el)}
                    positions={totalRoute}
                    {...planLineStyle}
                    eventHandlers={this.polylineEvents("main")}
                />
                {selectGeozonePoint && (
                    <CircleMarker
                        center={[selectGeozonePoint[1], selectGeozonePoint[0]]}
                        {...taskMarkerStyle}
                        interactive={false}
                    />
                )}
                {selectGeozoneMode && selectGeozoneMode !== "form" && (
                    <MapMoveHandler
                        setLinePoint={(selectGeozonePoint) => this.setState({ selectGeozonePoint })}
                        lines={{ main: this.mainLine, empty: this.emptyLine }}
                        mouseOnLine={this.state.mouseOnLine}
                    />
                )}
                {!selectGeozoneMode && (
                    <>
                        {tollRoads && this.tollRoads()}
                        {traveledRoute &&
                            traveledRoute.map((route, index) => {
                                if (!(route.isMobile ? mobile : tracker)) {
                                    return null;
                                }
                                const line =
                                    route.hasActiveMileage && !expandedRangeTime
                                        ? route.isMobile
                                            ? mobileFactLineStyle
                                            : factLineStyle
                                        : actualLineStyle;
                                const lineOptions = {
                                    ...line.style,
                                    color: line.colors[index % line.colors.length],
                                };
                                return (
                                    <Fragment key={index}>
                                        <PolylineDecorator
                                            positions={route.points[routeTypesEnum.Main]}
                                            {...lineOptions}
                                        />
                                        <PolylineDecorator
                                            positions={route.points[routeTypesEnum.EmptyMileage]}
                                            {...factEmptyDashLineStyle}
                                        />
                                        {route.emulatedSegments.map((item, i) => (
                                            <PolylineDecorator
                                                key={i}
                                                positions={item.coordinates}
                                                {...(item.routeType = routeTypesEnum.Main
                                                    ? emulatedLineStyle
                                                    : emulatedEmptyDashLineStyle)}
                                                color={
                                                    item.id === this.state.selectedEmulatedSegment
                                                        ? "#904900"
                                                        : "#c16200"
                                                }
                                            >
                                                {(isRoot ||
                                                    isTruckingOrganizationAdmin ||
                                                    isTruckingOrganizationManager ||
                                                    isTruckingOrganizationHeadLogist) && (
                                                    <Popup
                                                        closeOnClick
                                                        autoPan={false}
                                                        eventHandlers={{
                                                            add: () =>
                                                                this.setState({ selectedEmulatedSegment: item.id }),
                                                            remove: () =>
                                                                this.setState({ selectedEmulatedSegment: undefined }),
                                                        }}
                                                    >
                                                        Хотите удалить эмулированный сегмент?
                                                        <br />
                                                        <div className="text-right pt-2">
                                                            <div
                                                                className="btn btn-xs btn-outline-danger"
                                                                onClick={() =>
                                                                    this.removeEmulatedSegment(item.id, bidId)
                                                                }
                                                            >
                                                                Удалить
                                                            </div>
                                                        </div>
                                                    </Popup>
                                                )}
                                            </PolylineDecorator>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        {organizationTimezone && organizationTimezone.id && zoom > 7 && (
                            <RoutePoints
                                key={tracker}
                                routePoints={routePoints || []}
                                organizationTimezone={organizationTimezone}
                            />
                        )}
                        {!this.props.isPreview && this.supportPoints()}
                    </>
                )}
            </>
        );
    }
}
